import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class KamaCartAdditions extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.addEventListener('submit', this.addProduct, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  addProduct = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.kg-cart-addon__form')) return;
    event.preventDefault();
    if (this.busy) return;
    this.busy = true;
    const button = form.querySelector('button');
    const status = this.querySelector('[role="status"]');
    const label = button.querySelector('span');
    const originalLabel = label.textContent;
    const variantId = form.elements.id.value;
    const quantity = Number(form.elements.quantity.value) || 1;
    const context = this.dataset.context;
    const restoreFocus = document.activeElement === button;
    const fallbackError = this.dataset.error;
    const refreshError = this.dataset.refreshError;
    const addedMessage = this.dataset.added;
    const refreshLabel = this.dataset.refresh;
    const buttons = [...this.querySelectorAll('.kg-cart-add[type="submit"]')];
    buttons.forEach(control => { control.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    label.textContent = button.dataset.adding;
    status.textContent = '';
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(e => e.dataset.sectionId))].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [{ merchandiseId: variantId, quantity }], promise: deferred.promise }));
    let committed = false;
    let refreshed = false;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sectionIds, sections_url: window.location.pathname + window.location.search })
      });
      const result = (await response.json().catch(() => null)) || {};
      if (!response.ok || result.status) throw new Error(result.description || result.message || fallbackError);
      committed = true;
      let cart;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
          if (!cartResponse.ok) throw new Error(refreshError);
          cart = await cartResponse.json();
          break;
        } catch (error) { if (attempt === 1) throw error; }
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections, source: 'kama-cart-additions', didError: false } });
      refreshed = true;
      status.textContent = addedMessage;
      if (restoreFocus) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const scope = context === 'drawer' ? document.querySelector('#cart-drawer') : document.querySelector('.kg-cart-page');
          const focus = scope?.querySelector('kama-cart-additions h2') || scope?.querySelector('.cart__checkout-button');
          focus?.focus({ preventScroll: true });
        }));
      }
    } catch (error) {
      deferred.reject(error);
      status.textContent = committed ? refreshError : (error.message || fallbackError);
      this.dispatchEvent(new CartErrorEvent({ error: status.textContent, code: 'INVALID' }));
      if (committed) {
        const refresh = document.createElement('button');
        refresh.type = 'button';
        refresh.className = 'kg-cart-refresh';
        refresh.textContent = refreshLabel;
        refresh.addEventListener('click', () => window.location.reload());
        status.append(' ', refresh);
      }
    } finally {
      if (!committed || refreshed) {
        buttons.forEach(control => { control.disabled = false; });
        button.removeAttribute('aria-busy');
        label.textContent = originalLabel;
        this.busy = false;
      }
    }
  };
}

if (!customElements.get('kama-cart-additions')) customElements.define('kama-cart-additions', KamaCartAdditions);
